import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight } from "lucide-react";
import { type NewsItem } from "@/hooks/useNews";

interface NewsProps {
  news: NewsItem[];
  heroMode?: boolean;
}

export const News = ({ news, heroMode = false }: NewsProps) => {
  const { language } = useLanguage();

  const getTitle = (item: NewsItem) => {
    if (language === "lv" && item.title_lv) return item.title_lv;
    if (language === "ru" && item.title_ru) return item.title_ru;
    return item.title_en;
  };

  const getBody = (item: NewsItem) => {
    if (language === "lv" && item.body_lv) return item.body_lv;
    if (language === "ru" && item.body_ru) return item.body_ru;
    return item.body_en;
  };

  const sectionTitle = {
    en: "Latest News",
    lv: "Jaunumi",
    ru: "Новости",
  };

  if (news.length === 0) return null;

  // Hero mode: full-width carousel/slider style replacing hero
  if (heroMode) {
    return (
      <section className="relative bg-charcoal">
        {news.length === 1 ? (
          <SingleNewsHero item={news[0]} getTitle={getTitle} getBody={getBody} language={language} />
        ) : (
          <MultiNewsHero items={news} getTitle={getTitle} getBody={getBody} language={language} />
        )}
      </section>
    );
  }

  // Standard section mode
  return (
    <section className="bg-charcoal py-16 lg:py-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-display text-cream tracking-wide">
            {sectionTitle[language] || sectionTitle.en}
          </h2>
          <div className="mt-4 mx-auto w-16 h-px bg-gold/50" />
        </div>

        <div
          className={`grid gap-8 ${
            news.length === 1
              ? "grid-cols-1 max-w-2xl mx-auto"
              : news.length === 2
              ? "grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto"
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {news.map((item) => {
            const title = getTitle(item);
            const body = getBody(item);
            const Wrapper = item.link_url ? "a" : "div";
            const wrapperProps = item.link_url
              ? { href: item.link_url, target: "_blank" as const, rel: "noopener noreferrer" }
              : {};

            return (
              <Wrapper
                key={item.id}
                {...wrapperProps}
                className={`group bg-charcoal-light border border-gold/10 rounded-lg overflow-hidden transition-all duration-300 ${
                  item.link_url
                    ? "hover:border-gold/30 hover:shadow-lg hover:shadow-gold/5 cursor-pointer"
                    : ""
                }`}
              >
                {item.image_url && (
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-display text-cream mb-3 group-hover:text-gold transition-colors">
                    {title}
                  </h3>
                  {body && (
                    <p className="text-cream/70 text-sm leading-relaxed line-clamp-4">
                      {body}
                    </p>
                  )}
                  {item.link_url && (
                    <div className="mt-4 flex items-center gap-2 text-gold text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      <span>
                        {language === "lv"
                          ? "Lasīt vairāk"
                          : language === "ru"
                          ? "Подробнее"
                          : "Read more"}
                      </span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  )}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── Hero sub-components ─────────────────────────────────────

import { useState, useEffect, useCallback } from "react";

interface HeroItemProps {
  item: NewsItem;
  getTitle: (i: NewsItem) => string;
  getBody: (i: NewsItem) => string | null;
  language: string;
}

const SingleNewsHero = ({ item, getTitle, getBody, language }: HeroItemProps) => {
  const title = getTitle(item);
  const body = getBody(item);

  const content = (
    <div className="relative min-h-screen flex items-center overflow-hidden">
      {item.image_url && (
        <img
          src={item.image_url}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-charcoal/20" />
      <div className="relative z-10 container mx-auto px-4 py-16">
        <div className="flex items-center min-h-[50vh]">
          <div className="max-w-xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-cream leading-[0.95] mb-6 drop-shadow-lg uppercase animate-slide-up">
              {title}
            </h1>
            {body && (
              <p className="text-lg md:text-xl text-cream/60 leading-relaxed max-w-md mb-10">
                {body}
              </p>
            )}
            {item.link_url && (
              <div className="inline-flex items-center gap-2 text-gold font-medium text-lg hover:gap-3 transition-all">
                <span>
                  {language === "lv" ? "Lasīt vairāk" : language === "ru" ? "Подробнее" : "Read more"}
                </span>
                <ArrowRight className="h-5 w-5" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (item.link_url) {
    return (
      <a href={item.link_url} target="_blank" rel="noopener noreferrer" className="block">
        {content}
      </a>
    );
  }
  return content;
};

interface MultiHeroProps {
  items: NewsItem[];
  getTitle: (i: NewsItem) => string;
  getBody: (i: NewsItem) => string | null;
  language: string;
}

const MultiNewsHero = ({ items, getTitle, getBody, language }: MultiHeroProps) => {
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((p) => (p + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    const interval = setInterval(next, 6000);
    return () => clearInterval(interval);
  }, [next]);

  const item = items[current];
  const title = getTitle(item);
  const body = getBody(item);

  const content = (
    <div className="relative min-h-screen flex items-center overflow-hidden">
      {/* All images stacked for crossfade */}
      {items.map((it, idx) => (
        <img
          key={it.id}
          src={it.image_url || ""}
          alt={getTitle(it)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            idx === current ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-charcoal/20" />

      <div className="relative z-10 container mx-auto px-4 pt-24 pb-16">
        <div className="flex items-center min-h-[80vh]">
          <div className="max-w-xl">
            <h1
              key={item.id + "-title"}
              className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-cream leading-[0.95] mb-6 drop-shadow-lg uppercase animate-slide-up"
            >
              {title}
            </h1>
            {body && (
              <p
                key={item.id + "-body"}
                className="text-lg md:text-xl text-cream/60 leading-relaxed max-w-md mb-10 animate-fade-in"
              >
                {body}
              </p>
            )}
            {item.link_url && (
              <a
                href={item.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gold font-medium text-lg hover:gap-3 transition-all"
              >
                <span>
                  {language === "lv" ? "Lasīt vairāk" : language === "ru" ? "Подробнее" : "Read more"}
                </span>
                <ArrowRight className="h-5 w-5" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {items.map((_, idx) => (
          <button
            key={idx}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCurrent(idx);
            }}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              idx === current ? "bg-gold w-6" : "bg-cream/40 hover:bg-cream/60"
            }`}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );

  return content;
};
